import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  MagnifyingGlass,
  Phone,
  UserPlus,
  Alarm,
  FileText,
  Folder,
  ChartLineUp,
  ChatCenteredDots,
  Shield,
  Palette,
  ChartBar,
  Gear,
  CaretRight
} from '@phosphor-icons/react';

// Custom Settings Components
import CallingAgentReports from '../components/settings/CallingAgentReports';
import CreateClient from '../components/settings/CreateClient';
import ClientsDirectory from '../components/settings/ClientsDirectory';
import Reports from '../components/settings/Reports';
import StatusManagement from '../components/StatusManagement';
import BlockedClientsSection from '../components/BlockedClientsSection';
import Appearance from '../components/settings/Appearance';
import ServiceTimelines from '../components/settings/ServiceTimelines';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_SETTINGS_POWERS = {
  client_list: true,
  client_directory: true,
  create_client: true,
  appearance: false,
  broadcast: true,
  status: true,
  reports: true,
  service_timelines: false,
  mobile_number_full: false,
  enquiry_handler_mb: true
};

const HANDLER_SETTINGS_CARDS = [
  { id: 'inactive_clients', title: 'Inactive Clients Tracker', desc: 'Track inactive clients & calling activity', icon: Alarm, color: 'text-orange-500', bg: 'bg-orange-50', powerKey: 'inactive_clients' },
  { id: 'calling_agent_reports', title: 'Calling Agent Reports', desc: 'Call/WhatsApp activity · sent reports', icon: Phone, color: 'text-pink-500', bg: 'bg-pink-50', powerKey: 'calling_agent_reports' },
  { id: 'create_client', title: 'Create Client Account', desc: 'Add new customer account', icon: UserPlus, color: 'text-green-500', bg: 'bg-green-50', powerKey: 'create_client' },
  { id: 'reports', title: 'Deposit / Withdraw Reports', desc: 'Excel · PDF · MB-wise reports', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', powerKey: 'reports' },
  { id: 'client_directory', title: 'Clients Directory', desc: 'MB-wise · WhatsApp Broadcast', icon: Folder, color: 'text-cyan-500', bg: 'bg-cyan-50', powerKey: 'client_directory' },
  { id: 'status', title: 'My Status Management', desc: 'Publish status updates & stories', icon: ChartLineUp, color: 'text-emerald-600', bg: 'bg-emerald-50', powerKey: 'status' },
  { id: 'blocked_clients', title: 'Blocked Clients', desc: 'Manage blocked customer list', icon: Shield, color: 'text-red-600', bg: 'bg-red-50', powerKey: 'blocked_clients' },
  { id: 'appearance', title: 'Appearance', desc: 'Theme & display preferences', icon: Palette, color: 'text-purple-500', bg: 'bg-purple-50', powerKey: 'appearance' },
  { id: 'service_timelines', title: 'Service Timelines', desc: 'Deposit · Withdraw · Support requests', icon: ChartBar, color: 'text-blue-500', bg: 'bg-blue-50', powerKey: 'service_timelines' }
];

export default function HandlerSettings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(() => {
    const sec = searchParams.get('section');
    return sec || null;
  });
  const [inquiryChats, setInquiryChats] = useState([]);
  const [loadingInquiries, setLoadingInquiries] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadInquiryChats = async () => {
    setLoadingInquiries(true);
    try {
      const response = await axios.get(`${API}/chat/inquiry-rooms`);
      setInquiryChats(response.data || []);
    } catch (err) {
      console.error('Failed to load inquiry chats:', err);
    } finally {
      setLoadingInquiries(false);
    }
  };

  const [livePowers, setLivePowers] = useState(user?.settings_powers || null);

  useEffect(() => {
    const fetchLatestPowers = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        if (res.data?.user?.settings_powers) {
          setLivePowers(res.data.user.settings_powers);
          const savedUser = localStorage.getItem('dws_user');
          if (savedUser) {
            const parsed = JSON.parse(savedUser);
            parsed.settings_powers = res.data.user.settings_powers;
            localStorage.setItem('dws_user', JSON.stringify(parsed));
          }
        }
      } catch (err) {
        console.error('Failed to fetch user settings powers:', err);
      }
    };
    fetchLatestPowers();
  }, []);

  const activePowers = livePowers !== null && livePowers !== undefined ? livePowers : user?.settings_powers;
  const userPowers = activePowers || {};

  const filteredCards = HANDLER_SETTINGS_CARDS.filter((card) => {
    const matchesSearch =
      card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      card.desc.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    if (user?.role === 'calling_handler') return card.id === 'inactive_clients' || card.id === 'calling_agent_reports';
    if (user?.role === 'super_admin') return true;
    return userPowers[card.powerKey] === true || userPowers[card.powerKey] === 'true';
  });

  const handleCardClick = (id) => {
    if (id === 'inactive_clients') {
      navigate('/inactive-clients');
    } else if (id === 'inquiry_chats') {
      loadInquiryChats();
      setActiveSection('inquiry_chats');
    } else {
      setActiveSection(id);
    }
  };

  // Uniform Green Header Sub-Page Wrapper
  const SubPageHeaderWrapper = ({ title, children }) => (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <div className="bg-[#00b87c] text-white px-6 py-4 flex items-center justify-between shadow-md select-none flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveSection(null)}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            title="Back to Handler Settings"
          >
            <ArrowLeft size={22} weight="bold" />
          </button>
          <div>
            <h1 className="text-lg font-bold leading-tight">{title}</h1>
            <p className="text-xs text-emerald-100 font-medium">Control Center · Admin Granted Access</p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 max-w-5xl w-full mx-auto space-y-4">
        {children}
      </div>
    </div>
  );

  if (activeSection === 'calling_agent_reports') {
    return <CallingAgentReports onBack={() => setActiveSection(null)} />;
  }

  if (activeSection === 'create_client') {
    return (
      <SubPageHeaderWrapper title="Create Client Account">
        <CreateClient onBack={() => setActiveSection(null)} />
      </SubPageHeaderWrapper>
    );
  }

  if (activeSection === 'reports') {
    return (
      <div className="h-screen w-screen bg-white fixed inset-0 z-[100] p-1.5 md:p-3 overflow-hidden">
        <Reports onClose={() => setActiveSection(null)} />
      </div>
    );
  }

  if (activeSection === 'client_directory') {
    return (
      <SubPageHeaderWrapper title="Clients Directory">
        <ClientsDirectory onBack={() => setActiveSection(null)} />
      </SubPageHeaderWrapper>
    );
  }

  if (activeSection === 'appearance') {
    return (
      <SubPageHeaderWrapper title="Appearance">
        <Appearance onBack={() => setActiveSection(null)} />
      </SubPageHeaderWrapper>
    );
  }

  if (activeSection === 'service_timelines') {
    return <ServiceTimelines onBack={() => setActiveSection(null)} />;
  }

  if (activeSection === 'status') {
    return (
      <SubPageHeaderWrapper title="My Status Management">
        <StatusManagement user={user} />
      </SubPageHeaderWrapper>
    );
  }

  if (activeSection === 'blocked_clients') {
    return (
      <SubPageHeaderWrapper title="Blocked Clients">
        <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-6">
          <BlockedClientsSection userId={user?.id} userRole={user?.role} />
        </div>
      </SubPageHeaderWrapper>
    );
  }

  if (activeSection === 'inquiry_chats') {
    return (
      <SubPageHeaderWrapper title="Inquiry Conversations">
        <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4">
          {loadingInquiries ? (
            <p className="p-4 text-center text-slate-400 text-sm animate-pulse">Loading inquiry chats...</p>
          ) : inquiryChats.length === 0 ? (
            <p className="p-4 text-center text-slate-500 text-sm">No inquiry chats found</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {inquiryChats.map((chat) => (
                <div
                  key={chat.room_id}
                  onClick={() => navigate(`/handler-chat/${chat.room_id}`)}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-600 font-bold text-sm">
                      {(chat.customer?.name || 'I')[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-900 truncate text-sm">{chat.customer?.name || 'Inquiry Client'}</h4>
                    <p className="text-xs text-slate-500 truncate">{chat.last_message?.text || 'No messages'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SubPageHeaderWrapper>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      {/* Top Header Bar */}
      <div className="bg-[#00b87c] text-white px-6 py-4 flex items-center justify-between shadow-md select-none">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/handler-dashboard')}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft size={22} weight="bold" />
          </button>
          <div>
            <h1 className="text-lg font-bold leading-tight">Handler Settings</h1>
            <p className="text-xs text-emerald-100 font-medium">Control Center · Admin Granted Access</p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 p-4 sm:p-6 max-w-5xl w-full mx-auto space-y-5">
        {/* Search Bar */}
        <div className="relative">
          <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search settings... (Ctrl+K)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00b87c] shadow-2xs transition-all placeholder-slate-400"
          />
        </div>

        {/* Cards Grid */}
        {filteredCards.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 space-y-2">
            <Gear size={36} className="mx-auto text-slate-300" weight="light" />
            <p className="font-bold text-sm text-slate-700">No settings available</p>
            <p className="text-xs text-slate-400">Settings menu items are assigned per handler by Admin</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {filteredCards.map((card) => {
              const IconComp = card.icon;
              return (
                <div
                  key={card.id}
                  onClick={() => handleCardClick(card.id)}
                  className="bg-white border border-slate-200/80 hover:border-[#00b87c] p-4 rounded-xl shadow-2xs hover:shadow-md cursor-pointer transition-all flex items-center justify-between group select-none"
                >
                  <div className="flex items-center gap-3.5">
                    <div className={`p-2.5 ${card.bg} ${card.color} rounded-xl flex-shrink-0 transition-transform group-hover:scale-105`}>
                      <IconComp size={22} weight="bold" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-sm group-hover:text-[#00b87c] transition-colors">
                        {card.title}
                      </h3>
                      <p className="text-xs text-slate-500 leading-tight mt-0.5">
                        {card.desc}
                      </p>
                    </div>
                  </div>
                  <CaretRight size={18} className="text-slate-300 group-hover:text-[#00b87c] group-hover:translate-x-0.5 transition-all" weight="bold" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
